/* import __COLOCATED_TEMPLATE__ from './emoji-action.hbs'; */
/* RESPONSIBLE TEAM: team-inbox */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { type ComposerPublicAPI } from '@intercom/composer-ember';
import storage from 'embercom/vendor/intercom/storage';
import { type DisplayContext } from 'embercom/services/command-k';
import type CustomEmojiService from 'embercom/services/custom-emoji-service';
import type { CustomEmoji } from 'embercom/services/custom-emoji-service';

// @ts-ignore
import intermoji from '@intercom/intermoji';

interface Args {
  composerApi?: ComposerPublicAPI;
  priority?: number;
  trackingData?: Record<string, unknown>;
  activatingComponent?: `${DisplayContext}`;
}

interface Signature {
  Args: Args;
}

export default class EmojiAction extends Component<Signature> {
  @service declare customEmojiService: CustomEmojiService;
  @service declare intercomEventService: $TSFixMe;

  @action insertEmoji(emojiValue: string | CustomEmoji) {
    // standard emojis can be added as text
    // custom emojis need to be added as an inline image
    if ((emojiValue as CustomEmoji).isCustomEmoji) {
      this.trackCustomEmojiInsertion(emojiValue as CustomEmoji);
      this.insertCustomEmoji(emojiValue as CustomEmoji);
    } else {
      this.insertStandardEmoji(emojiValue as string);
    }
  }

  private trackCustomEmojiInsertion(customEmoji: CustomEmoji): void {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: 'custom_emoji',
      section: 'composer',
      emoji_identifier: customEmoji.identifier,
      emoji_type: 'custom',
      insertion_method: 'picker',
    });
  }

  private insertCustomEmoji(customEmoji: CustomEmoji) {
    // Insert custom emoji as inline image
    let composer = this.args.composerApi?.composer;
    if (composer) {
      let state = composer.state.prosemirrorState;

      let inlineImageNode = state.schema.nodes.inlineImage.create({
        src: customEmoji.src,
        text: customEmoji.alt || customEmoji.identifier,
        type: 'inlineImage',
        className: 'custom-emoji',
      });

      let tr = state.tr.replaceSelectionWith(inlineImageNode);
      composer.commands.dispatch(tr);

      composer.commands.focus();
    }

    // Store usage for frequently used emoji category
    this.persistCustomEmoji(customEmoji);
  }

  private insertStandardEmoji(emoji: string) {
    this.persistEmoji(emoji);
    this.args.composerApi?.composer.commands.focus();
    this.args.composerApi?.composer.commands.insertText(emoji);
  }

  private persistCustomEmoji(customEmoji: CustomEmoji) {
    let emojis = storage.get('userEmojis') ?? [];
    emojis.unshift(`${customEmoji.identifier}`);
    storage.set('userEmojis', emojis);
  }

  private persistEmoji(emoji: string) {
    let emojis = storage.get('userEmojis') ?? [];
    emojis.unshift(intermoji.identifierFromUnicode(emoji));

    storage.set('userEmojis', emojis);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::Composer::CommandK::EmojiAction': typeof EmojiAction;
    'inbox2/composer/command-k/emoji-action': typeof EmojiAction;
  }
}
